/**
 * Шаг 3 wizard «Статус» (E07.3, ФТ-1.3).
 * Уровень экспертизы по Figma Potok / node 1:759.
 */

import { useEffect, useMemo, useState } from "react";
import {
  DIALOGUE_STATUS_LABELS,
  type DialogueStatus,
  type PublicIndustry,
  type PublicModelCard,
} from "@potok/shared";
import type { PublicCatalogApi } from "../../api/catalog.api";
import { ApiRequestError } from "../../api/http";
import statusChevron from "../../assets/status-chevron.svg";
import type { ModelSelection } from "./ModelsStep";

export type StatusStepProps = {
  api: PublicCatalogApi;
  industryId: string | null;
  selectedModels: readonly ModelSelection[];
  status: DialogueStatus | null;
  onStatusChange: (status: DialogueStatus) => void;
  onBack: () => void;
  /** Выбор карточки сразу продолжает wizard (передаёт выбранный статус). */
  onContinue: (status: DialogueStatus) => void;
  /** Заглушка до E08: клик по предложению регистрации. */
  onRegister?: () => void;
};

const STATUS_OPTIONS: DialogueStatus[] = ["beginner", "peer", "expert"];

export function StatusStep({
  api,
  industryId,
  selectedModels,
  status,
  onStatusChange,
  onBack,
  onContinue,
  onRegister,
}: StatusStepProps) {
  const [industries, setIndustries] = useState<PublicIndustry[]>([]);
  const [models, setModels] = useState<PublicModelCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [registerHint, setRegisterHint] = useState(false);

  const skippedCatalog = industryId === null && selectedModels.length === 0;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([api.listIndustries(), api.listModels(industryId)])
      .then(([loadedIndustries, loadedModels]) => {
        if (!cancelled) {
          setIndustries(loadedIndustries);
          setModels(loadedModels);
        }
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(describeError(caught));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api, industryId]);

  const industryName = useMemo(() => {
    if (industryId === null) {
      return null;
    }
    return industries.find((item) => item.id === industryId)?.name ?? "Выбранная отрасль";
  }, [industries, industryId]);

  const modelRows = useMemo(() => {
    return selectedModels.map((item, index) => {
      const model = models.find((row) => row.id === item.modelId);
      const unitCode =
        item.preferredUnitId === null
          ? null
          : (model?.units.find((unit) => unit.id === item.preferredUnitId)?.code ?? null);
      return {
        order: index + 1,
        name: model?.name ?? "Модель",
        unitCode,
      };
    });
  }, [models, selectedModels]);

  const summaryModels =
    modelRows.length === 0
      ? "Не выбраны"
      : modelRows
          .map((row) => (row.unitCode ? `${row.name} · ${row.unitCode}` : row.name))
          .join(", ");

  function pickStatus(option: DialogueStatus): void {
    onStatusChange(option);
    onContinue(option);
  }

  return (
    <section className="wizard-step wizard-step--status" aria-labelledby="status-step-title">
      <div className="status-summary" aria-live="polite">
        <span className="status-summary__label">Ваш выбор:</span>
        <span className="status-summary__value">{industryName ?? "Отрасль не выбрана"}</span>
        <span className="status-summary__arrow" aria-hidden="true">
          →
        </span>
        <span className="status-summary__value">{summaryModels}</span>
      </div>

      <header className="status-header">
        <h2 id="status-step-title" className="wizard-step__title">
          Ваш уровень экспертизы
        </h2>
        <p className="wizard-step__hint">
          Шаг 3 из 5 — Система адаптирует форму расчёта под ваш опыт
        </p>
      </header>

      {error ? (
        <p className="wizard-step__status wizard-step__status--error" role="alert">
          {error}
        </p>
      ) : null}
      {loading ? <p className="wizard-step__hint">Загрузка сводки…</p> : null}

      <div className="status-options" role="radiogroup" aria-label="Уровень диалога">
        {STATUS_OPTIONS.map((option, index) => {
          const selected = status === option;
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={selected}
              className={selected ? "status-option status-option--selected" : "status-option"}
              disabled={loading}
              onClick={() => pickStatus(option)}
            >
              <span className="status-option__index" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <span className="status-option__body">
                <span className="status-option__title-row">
                  <span className="status-option__label">{DIALOGUE_STATUS_LABELS[option]}</span>
                  <span className="status-option__tag">{statusTag(option)}</span>
                </span>
                <span className="status-option__hint">{statusHint(option)}</span>
              </span>
              <img
                className="status-option__chevron"
                src={statusChevron}
                alt=""
                width={20}
                height={20}
                aria-hidden="true"
              />
            </button>
          );
        })}
      </div>

      {skippedCatalog ? (
        <aside className="register-offer" aria-labelledby="register-offer-title">
          <h3 id="register-offer-title">Зарегистрируйтесь</h3>
          <p>
            Вы не указали отрасль и модели. Регистрация сохранит историю подбора, откроет доступ к
            результатам и позволит вернуться к расчётам по персональному коду.
          </p>
          <ul>
            <li>Сохранение проектов и шагов</li>
            <li>Повторный вход по 8-значному коду</li>
            <li>Допуски к прайсу и ведомости после согласования</li>
          </ul>
          <button
            type="button"
            className="wizard-step__primary"
            onClick={() => {
              setRegisterHint(true);
              onRegister?.();
            }}
          >
            Перейти к регистрации
          </button>
          {registerHint ? (
            <p className="wizard-step__hint" role="status">
              Форма регистрации появится в следующем эпике (E08). Можно продолжить как гость.
            </p>
          ) : null}
        </aside>
      ) : null}

      <div className="wizard-step__actions">
        <button type="button" className="wizard-step__back" onClick={onBack}>
          ← Назад
        </button>
      </div>
    </section>
  );
}

function statusTag(status: DialogueStatus): string {
  switch (status) {
    case "beginner":
      return "Упрощённая форма, максимальные подсказки";
    case "peer":
      return "Полная форма параметров, доступна экскурсия по оборудованию";
    case "expert":
      return "Расширенная форма с формулами, все технические поля";
  }
}

function statusHint(status: DialogueStatus): string {
  switch (status) {
    case "beginner":
      return "Впервые подбираю конвейерное оборудование. Нужно подобрать с нуля — укажу только требования к процессу.";
    case "peer":
      return "Имею опыт работы с конвейерами, могу обсуждать технические характеристики на профессиональном уровне.";
    case "expert":
      return "Инженер-конструктор или технолог. Знаю типоразмеры, могу указать точные параметры расчёта.";
  }
}

function describeError(error: unknown): string {
  if (error instanceof ApiRequestError) {
    return error.message;
  }
  return "Не удалось загрузить сводку выбора";
}
